import React, { useCallback, useMemo, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { showOKRDetails } from 'components/RabbitOKRSidePanel/okrPanelControlSlice';
import { useViewPageName } from 'components/Views/Hooks/useViewPageName';
import useOKRConfig from 'components/hooks/useOKRConfig';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { OwnersAndCollaborators } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CommonTreeCell/OwnersAndCollaborators';
import { generateKebabMenuOptionsRevamp } from 'screens/Team/Rabbit/Goals/AgGridTreeView/utils';

import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';

import { ObjectiveKRCell } from './ObjectiveKRCell';

const GoalsMySummaryAgGrid = ({
  data,
  refetchCascadedViewData = () => {},
  /* following prop is badly named. it is used to clip data to 3 rows. */
  selectedTimeline = {},
  isProfilePage = false,
}) => {
  const history = useHistory();
  const location = useLocation();
  const page = useViewPageName();
  const queryClient = useQueryClient();

  const gridRef = useRef();
  const dispatch = useDispatch();

  const { objectivesName, moduleName, keyResultsName } = useOrganisationSettings();

  const { OBJECTIVE_TYPE, IS_KR_CASCADING_ENABLED } = useOKRConfig();

  const rowData = useMemo(() => {
    const dataArray = [...data];
    const toBeReturnedDataArray = dataArray?.splice(0, 3);

    return toBeReturnedDataArray.map((row) => ({ ...row, timeBoundDetails: selectedTimeline }));
  }, [data, selectedTimeline]);

  const columnDefs = useMemo(
    () => [
      // we're using the auto group column by default!
      {
        field: 'toIds',
        width: 160,
        filter: true,
        sortable: true,
        headerName: 'Owner',
        // will be used for searching by owner and employee function
        valueGetter: (data) => data.data?.toIds?.map(({ displayName, employeeFunction }) => `${displayName}`),
        cellRenderer: OwnersAndCollaborators,
      },
      //   {
      //     field: 'progressLevel',
      //     headerName: 'Progress',
      //     filter: true,
      //     sortable: false,
      //     valueGetter: (data) => {
      //       return data.data.progressLevel;
      //     },
      //     filterParams: {
      //       values: ['ON_TRACK', 'OFF_TRACK', 'AT_RISK', 'CLOSED'],
      //       valueFormatter: ({ value }) => {
      //         return capitalize(startCase(value));
      //       }
      //     }
      //   }
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      autoHeight: true,
      wrapText: true,
    }),
    []
  );

  const autoGroupColumnDef = useMemo(
    () => ({
      headerName: 'Objectives',
      minWidth: 300,
      flex: 1,
      filter: true,
      cellStyle: { height: 'auto' },
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: ObjectiveKRCell,
      },
      //  hacky way to make sure that the auto group column is updated on the basis of the valueGetter
      valueGetter: (data) =>
        data?.data?.title +
        data?.data?.progressLevel +
        data?.data?.progress +
        data?.data?.avgProgressPercentage +
        data?.data?.visibilityType +
        data?.data?.ifGoalChildExists +
        data?.data?.progressType,
    }),
    []
  );
  const getDataPath = useMemo(() => (data) => data.hierarchy, []);

  const rowClickHandler = ({ data, column }) => {
    if (column.colId === '0') {
      // This is one way to prevent event bubbling.
      // Every column has a column id which is generally the header name.
      // In this case, the last header does not have a name, so the ag grid automatically gave it an ID which is 0(FOR KEBAB MENU).
    } else {
      const { type, uuid } = data ?? {};
      dispatch(
        showOKRDetails({
          id: uuid,
          type,
          onDelete: refetchCascadedViewData,
          onUpdate: refetchCascadedViewData,
          history,
        })
      );
    }
  };

  const getRowId = useCallback(({ data }) => data?.uuid, []);

  const getRowStyle = (params) => {
    if (params?.data?.unMasked) {
      return { cursor: 'pointer' };
    }
  };

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment

    <AgGridTable
      ref={gridRef}
      rowData={isProfilePage ? data : rowData}
      columnDefs={columnDefs}
      defaultColDef={defaultColDef}
      autoGroupColumnDef={autoGroupColumnDef}
      treeData
      animateRows={false}
      groupDefaultExpanded={-1}
      getDataPath={getDataPath}
      domLayout="autoHeight"
      onCellClicked={rowClickHandler}
      getRowId={getRowId}
      getRowStyle={getRowStyle}
      headerHeight={0}
      kebabMenuOptionsGenerator={(data) =>
        generateKebabMenuOptionsRevamp({
          page,
          data,
          dispatch,
          keyResultsName,
          objectivesName,
          queryClient,
          IS_KR_CASCADING_ENABLED,
          OBJECTIVE_TYPE,
          history,
          location,
          // selectedTimelines
        })
      }
      kebabMenuIsRowSpecific
      containerAutoHeight
    />
  );
};

export { GoalsMySummaryAgGrid };
